/**
 * Apple TV → Screens. A direct port of the corporate dashboard's
 * appletv-screens-manager: Screens and Screen Groups tabs, inline icon actions,
 * the same Pair / Add Screen / Add Group dialogs and member management. Only
 * the styling is Prospera's.
 *
 * WHAT IS ACTUALLY PLAYING is not simply `screen.playlistId`. A screen in a
 * group whose group has a playlist shows the group's; the Playlist column
 * resolves that, and the screen dialog warns about it.
 *
 * The playlist pickers list only this account's own playlists (GET /playlists
 * is per user). A screen or group can still be playing one an administrator
 * shared with it: that is shown read-only with a badge, stays selectable as the
 * current value, and is gone from the list once the user switches away.
 */
import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Pencil, Plus, Search, Trash2, Tv, Unplug, Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import type { BadgeVariant } from '@/types/common.types';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/store/toastStore';
import type { SelectOption } from '@/types/common.types';
import {
  addScreensToGroup,
  createScreen,
  createScreenGroup,
  deleteScreen,
  deleteScreenGroup,
  listLocations,
  listPlaylists,
  listScreenGroups,
  listScreens,
  pairDevice,
  removeScreenFromGroup,
  unpairDevice,
  updateScreen,
  updateScreenGroup,
  type AppleTvLocation,
  type AppleTvPlaylist,
  type AppleTvScreen,
  type AppleTvScreenGroup,
} from '@/services/appleTv.service';
import { AppleTvGate, AppleTvPageShell, shouldGate, useAppleTvAccess } from './useAppleTvAccess';
import { IconAction, Table } from './AppleTvTable';
import {
  SHARED_PLAYLIST_WARNING,
  isNoLongerAvailable,
  sharedPlaylistName,
  signageError,
  signageErrorMessage,
  useSharedPlaylists,
} from './sharedPlaylists';
import { SharedPlaylistBadge, SharedPlaylistViewer } from './SharedPlaylistViewer';

const STATUS_VARIANT: Record<AppleTvScreen['status'], BadgeVariant> = {
  online: 'success',
  offline: 'neutral',
  unknown: 'warning',
};

function StatusBadge({ status }: { status: AppleTvScreen['status'] }) {
  return <Badge variant={STATUS_VARIANT[status]}>{status}</Badge>;
}

function fmtLastSeen(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

type ScreenForm = { name: string; locationId: number | null; screenGroupId: number | null; playlistId: number | null };
const emptyScreenForm: ScreenForm = { name: '', locationId: null, screenGroupId: null, playlistId: null };

type GroupForm = { name: string; locationId: number | null; description: string; playlistId: number | null };
const emptyGroupForm: GroupForm = { name: '', locationId: null, description: '', playlistId: null };

type PairForm = {
  pairingCode: string;
  screenName: string;
  locationId: number | null;
  screenGroupId: number | null;
  playlistId: number | null;
};
const emptyPairForm: PairForm = { pairingCode: '', screenName: '', locationId: null, screenGroupId: null, playlistId: null };

export default function AppleTvScreensPage() {
  const access = useAppleTvAccess();
  const { configured, canManage } = access;

  const [locations, setLocations] = useState<AppleTvLocation[]>([]);
  const [playlists, setPlaylists] = useState<AppleTvPlaylist[]>([]);
  const [screens, setScreens] = useState<AppleTvScreen[]>([]);
  const [groups, setGroups] = useState<AppleTvScreenGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Screen dialog
  const [screenDialogOpen, setScreenDialogOpen] = useState(false);
  const [editingScreen, setEditingScreen] = useState<AppleTvScreen | null>(null);
  const [screenForm, setScreenForm] = useState<ScreenForm>(emptyScreenForm);
  const [savingScreen, setSavingScreen] = useState(false);
  const [deleteScreenTarget, setDeleteScreenTarget] = useState<AppleTvScreen | null>(null);
  const [unpairTarget, setUnpairTarget] = useState<AppleTvScreen | null>(null);

  // Pair dialog
  const [pairOpen, setPairOpen] = useState(false);
  const [pairForm, setPairForm] = useState<PairForm>(emptyPairForm);
  const [pairing, setPairing] = useState(false);

  // Group dialog
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AppleTvScreenGroup | null>(null);
  const [groupForm, setGroupForm] = useState<GroupForm>(emptyGroupForm);
  const [savingGroup, setSavingGroup] = useState(false);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<AppleTvScreenGroup | null>(null);

  // A shared playlist being viewed, and a switch away from one awaiting confirmation.
  const [viewingShared, setViewingShared] = useState<number | null>(null);
  const [pendingSwitch, setPendingSwitch] = useState<(() => void) | null>(null);

  // Manage members dialog
  const [membersGroup, setMembersGroup] = useState<AppleTvScreenGroup | null>(null);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [savingMembers, setSavingMembers] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([listLocations(), listPlaylists(), listScreens(), listScreenGroups()])
      .then(([locs, pls, scr, grp]) => {
        setLocations(locs);
        setPlaylists(pls);
        setScreens(scr);
        setGroups(grp);
      })
      .catch(() => toast.error('Failed to load screens'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!configured) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured]);

  const shared = useSharedPlaylists(
    playlists,
    [...screens.map((s) => s.playlistId), ...groups.map((g) => g.playlistId)],
    configured && !loading,
  );
  /** Assigned to this screen or group by an administrator: not in the account's own list. */
  const isShared = (id: number | null | undefined): id is number =>
    id != null && !playlists.some((p) => p.id === id);

  const locName = (id: number | null | undefined) => locations.find((l) => l.id === id)?.name ?? '—';
  const playlistName = (id: number | null | undefined) =>
    id == null
      ? null
      : (playlists.find((p) => p.id === id)?.name ?? sharedPlaylistName(shared[id], id));
  const groupName = (id: number | null | undefined) =>
    id == null ? null : groups.find((g) => g.id === id)?.name ?? `#${id}`;

  const locationOptions = useMemo(
    () => locations.map((l) => ({ value: l.id, label: l.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [locations],
  );
  const playlistOptions = useMemo(
    () =>
      playlists
        .map((p) => ({ value: p.id, label: p.sharedWithMe ? `${p.name} (shared by Corporate)` : p.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [playlists],
  );
  /**
   * The account's own playlists, plus a shared one while it is still the
   * current selection: once the user switches away it is not offered again,
   * because only an administrator can assign it.
   */
  const playlistOptionsWith = (current: number | null): SelectOption<number>[] =>
    isShared(current)
      ? [
          { value: current, label: `${playlistName(current)} (shared by Corporate)` },
          ...playlistOptions,
        ]
      : playlistOptions;
  /** Switching away from a shared playlist asks first; anything else applies at once. */
  const changePlaylist = (current: number | null, next: number | null, apply: () => void) => {
    if (isShared(current) && next !== current) setPendingSwitch(() => apply);
    else apply();
  };

  /**
   * A failed save or pair. A 404 naming a playlist means it is not this
   * account's any more (or is gone): refresh the picker and say so. Anything
   * else, including a 403 on a shared playlist, shows the API's own message.
   */
  const reportPlaylistError = (error: unknown, fallback: string) => {
    if (isNoLongerAvailable(error) && /playlist/i.test(signageError(error).message ?? '')) {
      toast.error('That playlist is no longer available. Pick another one.');
      listPlaylists()
        .then(setPlaylists)
        .catch(() => undefined);
      return;
    }
    toast.error(signageErrorMessage(error, fallback));
  };

  /** A playlist's name, with the shared badge and a read-only View for a shared one. */
  const playlistCell = (id: number | null | undefined, suffix = '') => {
    if (id == null) return '—';
    if (!isShared(id)) return `${playlistName(id)}${suffix}`;
    return (
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate">
          {playlistName(id)}
          {suffix}
        </span>
        <SharedPlaylistBadge />
        <button
          type="button"
          className="shrink-0 text-xs font-medium text-primary hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            setViewingShared(id);
          }}
        >
          View
        </button>
      </span>
    );
  };
  const groupOptionsFor = (locationId: number | null) =>
    groups.filter((g) => g.locationId === locationId).map((g) => ({ value: g.id, label: g.name }));

  const effectivePlaylist = (s: AppleTvScreen) => {
    if (s.screenGroupId != null) {
      const g = groups.find((gr) => gr.id === s.screenGroupId);
      if (g && g.playlistId != null) return playlistCell(g.playlistId, ' (group)');
    }
    return playlistCell(s.playlistId);
  };

  const q = search.trim().toLowerCase();
  const filteredScreens = useMemo(() => {
    if (!q) return screens;
    return screens.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        locName(s.locationId).toLowerCase().includes(q) ||
        (s.deviceId ?? '').toLowerCase().includes(q),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screens, q, locations]);
  const filteredGroups = useMemo(() => {
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q) || locName(g.locationId).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, q, locations]);

  // ── Screen CRUD ──────────────────────────────────────────────
  const openCreateScreen = () => {
    setEditingScreen(null);
    setScreenForm(emptyScreenForm);
    setScreenDialogOpen(true);
  };
  const openEditScreen = (s: AppleTvScreen) => {
    setEditingScreen(s);
    setScreenForm({ name: s.name, locationId: s.locationId, screenGroupId: s.screenGroupId, playlistId: s.playlistId });
    setScreenDialogOpen(true);
  };
  const screenErrors = { name: !screenForm.name.trim(), locationId: screenForm.locationId == null };
  const screenGroupHasPlaylist =
    screenForm.screenGroupId != null && groups.find((g) => g.id === screenForm.screenGroupId)?.playlistId != null;

  const saveScreen = async () => {
    if (screenErrors.name || screenErrors.locationId) return;
    setSavingScreen(true);
    try {
      const payload = {
        name: screenForm.name.trim(),
        locationId: screenForm.locationId!,
        screenGroupId: screenForm.screenGroupId,
        playlistId: screenForm.playlistId,
      };
      if (editingScreen) {
        const updated = await updateScreen(editingScreen.id, payload);
        setScreens((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        toast.success('Screen updated');
      } else {
        const created = await createScreen(payload);
        setScreens((prev) => [...prev, created]);
        toast.success('Screen created');
      }
      setScreenDialogOpen(false);
    } catch (error) {
      reportPlaylistError(error, 'Failed to save screen');
    } finally {
      setSavingScreen(false);
    }
  };

  const doDeleteScreen = async () => {
    if (!deleteScreenTarget) return;
    try {
      await deleteScreen(deleteScreenTarget.id);
      setScreens((prev) => prev.filter((s) => s.id !== deleteScreenTarget.id));
      toast.success('Screen deleted');
    } catch {
      toast.error('Failed to delete screen');
    } finally {
      setDeleteScreenTarget(null);
    }
  };

  const doUnpair = async () => {
    if (!unpairTarget?.deviceId) return;
    try {
      await unpairDevice(unpairTarget.deviceId);
      setScreens((prev) =>
        prev.map((s) => (s.id === unpairTarget.id ? { ...s, deviceId: null, status: 'offline' } : s)),
      );
      toast.success('Device unpaired — the Apple TV will return to its pairing screen');
    } catch {
      toast.error('Failed to unpair device');
    } finally {
      setUnpairTarget(null);
    }
  };

  // ── Pairing ──────────────────────────────────────────────────
  const openPair = () => {
    setPairForm(emptyPairForm);
    setPairOpen(true);
  };
  const pairErrors = {
    code: !/^\d{6}$/.test(pairForm.pairingCode),
    screenName: !pairForm.screenName.trim(),
    locationId: pairForm.locationId == null,
  };
  const doPair = async () => {
    if (pairErrors.code || pairErrors.screenName || pairErrors.locationId) return;
    setPairing(true);
    try {
      await pairDevice({
        pairingCode: pairForm.pairingCode,
        screenName: pairForm.screenName.trim(),
        locationId: pairForm.locationId!,
        screenGroupId: pairForm.screenGroupId,
        playlistId: pairForm.playlistId,
      });
      toast.success('Device paired — the screen should start playing shortly');
      setPairOpen(false);
      load();
    } catch (error) {
      if (isNoLongerAvailable(error) && /playlist/i.test(signageError(error).message ?? '')) {
        reportPlaylistError(error, '');
      } else {
        toast.error('Pairing failed. Check the code on the TV and try again.');
      }
    } finally {
      setPairing(false);
    }
  };

  // ── Group CRUD ───────────────────────────────────────────────
  const openCreateGroup = () => {
    setEditingGroup(null);
    setGroupForm(emptyGroupForm);
    setGroupDialogOpen(true);
  };
  const openEditGroup = (g: AppleTvScreenGroup) => {
    setEditingGroup(g);
    setGroupForm({ name: g.name, locationId: g.locationId, description: g.description ?? '', playlistId: g.playlistId });
    setGroupDialogOpen(true);
  };
  const groupErrors = { name: !groupForm.name.trim(), locationId: groupForm.locationId == null };

  const saveGroup = async () => {
    if (groupErrors.name || groupErrors.locationId) return;
    setSavingGroup(true);
    try {
      if (editingGroup) {
        const updated = await updateScreenGroup(editingGroup.id, {
          name: groupForm.name.trim(),
          description: groupForm.description.trim() || null,
          playlistId: groupForm.playlistId,
        });
        setGroups((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
        toast.success('Group updated');
      } else {
        const created = await createScreenGroup({
          name: groupForm.name.trim(),
          locationId: groupForm.locationId!,
          description: groupForm.description.trim() || null,
          playlistId: groupForm.playlistId,
        });
        setGroups((prev) => [...prev, created]);
        toast.success('Group created');
      }
      setGroupDialogOpen(false);
    } catch (error) {
      reportPlaylistError(error, 'Failed to save group');
    } finally {
      setSavingGroup(false);
    }
  };

  const doDeleteGroup = async () => {
    if (!deleteGroupTarget) return;
    try {
      await deleteScreenGroup(deleteGroupTarget.id);
      const removedId = deleteGroupTarget.id;
      setGroups((prev) => prev.filter((g) => g.id !== removedId));
      setScreens((prev) => prev.map((s) => (s.screenGroupId === removedId ? { ...s, screenGroupId: null } : s)));
      toast.success('Group deleted');
    } catch {
      toast.error('Failed to delete group');
    } finally {
      setDeleteGroupTarget(null);
    }
  };

  // ── Members ──────────────────────────────────────────────────
  const openMembers = (g: AppleTvScreenGroup) => {
    setMembersGroup(g);
    setMemberIds(screens.filter((s) => s.screenGroupId === g.id).map((s) => String(s.id)));
  };
  const saveMembers = async () => {
    if (!membersGroup) return;
    setSavingMembers(true);
    try {
      const current = new Set(screens.filter((s) => s.screenGroupId === membersGroup.id).map((s) => s.id));
      const next = new Set(memberIds.map(Number));
      const toAdd = [...next].filter((id) => !current.has(id));
      const toRemove = [...current].filter((id) => !next.has(id));
      if (toAdd.length) await addScreensToGroup(membersGroup.id, toAdd);
      for (const id of toRemove) await removeScreenFromGroup(membersGroup.id, id);
      const [freshGroups, freshScreens] = await Promise.all([listScreenGroups(), listScreens()]);
      setGroups(freshGroups);
      setScreens(freshScreens);
      toast.success('Group members updated');
      setMembersGroup(null);
    } catch {
      toast.error('Failed to update members');
    } finally {
      setSavingMembers(false);
    }
  };

  const gated = shouldGate(access);

  const searchBox = (placeholder: string) => (
    <div className="relative min-w-[200px] flex-1">
      <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
      <Input placeholder={placeholder} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
    </div>
  );

  const screensTab = (
    <Table.Root className="mt-3">
      <Table.Toolbar>
        {searchBox('Search screens...')}
        {canManage && (
          <>
            <Button variant="secondary" leftIcon={<Unplug className="h-4 w-4" />} onClick={openPair}>
              Pair Device
            </Button>
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreateScreen}>
              Add Screen
            </Button>
          </>
        )}
      </Table.Toolbar>
      {loading ? (
        <Table.Loading />
      ) : (
        <Table.Content>
          <table className="w-full table-fixed">
            <Table.Header>
              <tr>
                <Table.Th className="w-[22%]">Name</Table.Th>
                <Table.Th>Location</Table.Th>
                <Table.Th>Group</Table.Th>
                <Table.Th>Playlist</Table.Th>
                <Table.Th className="w-24">Status</Table.Th>
                <Table.Th className="w-40">Last Seen</Table.Th>
                <Table.Th className="w-32 text-right">Actions</Table.Th>
              </tr>
            </Table.Header>
            <tbody className="divide-y divide-slate-200">
              {filteredScreens.length === 0 ? (
                <Table.Empty
                colSpan={7}
                message={
                  <span>
                    No screens yet.{' '}
                    <Link
                      to="/marketing/apple-tv/get-started"
                      className="font-medium text-primary hover:underline"
                    >
                      Set up your first screen
                    </Link>
                    .
                  </span>
                }
              />
              ) : (
                filteredScreens.map((s) => (
                  <Table.Row key={s.id} onClick={canManage ? () => openEditScreen(s) : undefined}>
                    <Table.Cell>
                      <span className="font-medium text-slate-900">{s.name}</span>
                    </Table.Cell>
                    <Table.Cell truncate>{locName(s.locationId)}</Table.Cell>
                    <Table.Cell truncate>{groupName(s.screenGroupId) ?? '—'}</Table.Cell>
                    <Table.Cell truncate>{effectivePlaylist(s)}</Table.Cell>
                    <Table.Cell>
                      <StatusBadge status={s.status} />
                    </Table.Cell>
                    <Table.Cell truncate>{fmtLastSeen(s.lastSeen)}</Table.Cell>
                    <Table.Cell actions>
                      {canManage && (
                        <>
                          <IconAction title="Edit" tone="primary" onClick={() => openEditScreen(s)}>
                            <Pencil className="h-4 w-4" />
                          </IconAction>
                          {s.deviceId && (
                            <IconAction title="Unpair device" tone="warning" onClick={() => setUnpairTarget(s)}>
                              <Unplug className="h-4 w-4" />
                            </IconAction>
                          )}
                          <IconAction title="Delete" tone="danger" onClick={() => setDeleteScreenTarget(s)}>
                            <Trash2 className="h-4 w-4" />
                          </IconAction>
                        </>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </tbody>
          </table>
        </Table.Content>
      )}
    </Table.Root>
  );

  const groupsTab = (
    <Table.Root className="mt-3">
      <Table.Toolbar>
        {searchBox('Search groups...')}
        {canManage && (
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreateGroup}>
            Add Group
          </Button>
        )}
      </Table.Toolbar>
      {loading ? (
        <Table.Loading />
      ) : (
        <Table.Content>
          <table className="w-full table-fixed">
            <Table.Header>
              <tr>
                <Table.Th className="w-[26%]">Name</Table.Th>
                <Table.Th>Location</Table.Th>
                <Table.Th>Playlist</Table.Th>
                <Table.Th className="w-24 text-center">Screens</Table.Th>
                <Table.Th className="w-40 text-right">Actions</Table.Th>
              </tr>
            </Table.Header>
            <tbody className="divide-y divide-slate-200">
              {filteredGroups.length === 0 ? (
                <Table.Empty colSpan={5} message="No screen groups yet." />
              ) : (
                filteredGroups.map((g) => (
                  <Table.Row key={g.id} onClick={canManage ? () => openEditGroup(g) : undefined}>
                    <Table.Cell>
                      <span className="font-medium text-slate-900">{g.name}</span>
                    </Table.Cell>
                    <Table.Cell truncate>{locName(g.locationId)}</Table.Cell>
                    <Table.Cell truncate>{playlistCell(g.playlistId)}</Table.Cell>
                    <Table.Cell className="text-center">
                      {screens.filter((s) => s.screenGroupId === g.id).length}
                    </Table.Cell>
                    <Table.Cell actions>
                      {canManage && (
                        <>
                          <IconAction title="Manage screens" tone="primary" onClick={() => openMembers(g)}>
                            <Users className="h-4 w-4" />
                          </IconAction>
                          <IconAction title="Edit" tone="primary" onClick={() => openEditGroup(g)}>
                            <Pencil className="h-4 w-4" />
                          </IconAction>
                          <IconAction title="Delete" tone="danger" onClick={() => setDeleteGroupTarget(g)}>
                            <Trash2 className="h-4 w-4" />
                          </IconAction>
                        </>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </tbody>
          </table>
        </Table.Content>
      )}
    </Table.Root>
  );

  return (
    <AppleTvPageShell>
      <div className="shrink-0 p-6 pb-0">
        <PageHeader title="Screens" subtitle="The displays in your waiting rooms, and the groups they belong to." />
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-6">
        {gated ? (
          <AppleTvGate {...access} />
        ) : (
          <Tabs
            className="min-h-0 flex-1"
            tabs={[
              { id: 'screens', label: 'Screens', icon: <Tv className="h-4 w-4" />, content: screensTab },
              { id: 'groups', label: 'Screen Groups', icon: <Users className="h-4 w-4" />, content: groupsTab },
            ]}
          />
        )}
      </div>

      {/* ── Screen create/edit dialog ── */}
      <Modal
        isOpen={screenDialogOpen}
        onClose={() => setScreenDialogOpen(false)}
        title={editingScreen ? 'Edit Screen' : 'Add Screen'}
        closeOnEscape={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setScreenDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveScreen} loading={savingScreen} disabled={screenErrors.name || screenErrors.locationId}>
              {editingScreen ? 'Update' : 'Create'}
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-slate-500">
          {editingScreen ? 'Update this screen.' : 'Manually register a screen (or pair a device instead).'}
        </p>
        <div className="grid gap-4">
          <FormField label="Name" required>
            <Input
              value={screenForm.name}
              onChange={(e) => setScreenForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g., Main Lobby Display"
            />
          </FormField>
          <FormField label="Location" required>
            <Select
              options={locationOptions}
              value={screenForm.locationId}
              onChange={(v) => setScreenForm((f) => ({ ...f, locationId: v, screenGroupId: null }))}
              placeholder="Choose a location"
              searchable
            />
          </FormField>
          <FormField label="Screen group">
            <Select
              options={groupOptionsFor(screenForm.locationId)}
              value={screenForm.screenGroupId}
              onChange={(v) => setScreenForm((f) => ({ ...f, screenGroupId: v }))}
              placeholder={screenForm.locationId != null ? 'None' : 'Choose a location first'}
              disabled={screenForm.locationId == null}
              clearable
              searchable
            />
          </FormField>
          <FormField label="Playlist">
            <Select
              options={playlistOptionsWith(screenForm.playlistId)}
              value={screenForm.playlistId}
              onChange={(v) =>
                changePlaylist(screenForm.playlistId, v, () => setScreenForm((f) => ({ ...f, playlistId: v })))
              }
              placeholder="None"
              clearable
              searchable
            />
            {isShared(screenForm.playlistId) && (
              <SharedSelectionNote onView={() => setViewingShared(screenForm.playlistId)} />
            )}
            {screenGroupHasPlaylist && (
              <p className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" /> This group has a playlist — it will play instead of the
                per-screen one.
              </p>
            )}
          </FormField>
        </div>
      </Modal>

      {/* ── Pair device dialog ── */}
      <Modal
        isOpen={pairOpen}
        onClose={() => setPairOpen(false)}
        title="Pair Apple TV"
        closeOnEscape={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setPairOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={doPair}
              loading={pairing}
              disabled={pairErrors.code || pairErrors.screenName || pairErrors.locationId}
            >
              Pair
            </Button>
          </div>
        }
      >
        <p className="mb-2 text-sm text-slate-500">
          Enter the 6-digit code shown on the Apple TV. This creates a screen.
        </p>
        <p className="mb-3 text-sm text-slate-500">
          Don&rsquo;t have the code?{' '}
          <Link to="/marketing/apple-tv/get-started" className="font-medium text-primary hover:underline">
            Install ProsperaSign on the Apple TV first
          </Link>
          .
        </p>
        <div className="grid gap-4">
          <FormField label="Pairing code" required>
            <Input
              value={pairForm.pairingCode}
              onChange={(e) =>
                setPairForm((f) => ({ ...f, pairingCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))
              }
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              className="text-center font-mono text-2xl tracking-[0.5em]"
              autoFocus
            />
          </FormField>
          <FormField label="Screen name" required>
            <Input
              value={pairForm.screenName}
              onChange={(e) => setPairForm((f) => ({ ...f, screenName: e.target.value }))}
              placeholder="e.g., Main Lobby Display"
            />
          </FormField>
          <FormField label="Location" required>
            <Select
              options={locationOptions}
              value={pairForm.locationId}
              onChange={(v) => setPairForm((f) => ({ ...f, locationId: v, screenGroupId: null }))}
              placeholder="Choose a location"
              searchable
            />
          </FormField>
          <FormField label="Screen group (optional)">
            <Select
              options={groupOptionsFor(pairForm.locationId)}
              value={pairForm.screenGroupId}
              onChange={(v) => setPairForm((f) => ({ ...f, screenGroupId: v }))}
              placeholder={pairForm.locationId != null ? 'None' : 'Choose a location first'}
              disabled={pairForm.locationId == null}
              clearable
              searchable
            />
          </FormField>
          <FormField label="Initial playlist (optional)">
            <Select
              options={playlistOptions}
              value={pairForm.playlistId}
              onChange={(v) => setPairForm((f) => ({ ...f, playlistId: v }))}
              placeholder="None"
              clearable
              searchable
            />
          </FormField>
        </div>
      </Modal>

      {/* ── Group create/edit dialog ── */}
      <Modal
        isOpen={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        title={editingGroup ? 'Edit Screen Group' : 'Add Screen Group'}
        closeOnEscape={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setGroupDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveGroup} loading={savingGroup} disabled={groupErrors.name || groupErrors.locationId}>
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-slate-500">A group's playlist plays on every member screen.</p>
        <div className="grid gap-4">
          <FormField label="Name" required>
            <Input
              value={groupForm.name}
              onChange={(e) => setGroupForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g., Waiting Room Wall"
            />
          </FormField>
          <FormField
            label="Location"
            required
            helpText={editingGroup ? "A group's location can't be changed after creation." : undefined}
          >
            {editingGroup ? (
              <Input value={locName(editingGroup.locationId)} readOnly disabled />
            ) : (
              <Select
                options={locationOptions}
                value={groupForm.locationId}
                onChange={(v) => setGroupForm((f) => ({ ...f, locationId: v }))}
                placeholder="Choose a location"
                searchable
              />
            )}
          </FormField>
          <FormField label="Description">
            <Textarea autoResize
              value={groupForm.description}
              onChange={(e) => setGroupForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional notes"
              rows={2}
            />
          </FormField>
          <FormField label="Playlist">
            <Select
              options={playlistOptionsWith(groupForm.playlistId)}
              value={groupForm.playlistId}
              onChange={(v) =>
                changePlaylist(groupForm.playlistId, v, () => setGroupForm((f) => ({ ...f, playlistId: v })))
              }
              placeholder="None"
              clearable
              searchable
            />
            {isShared(groupForm.playlistId) && (
              <SharedSelectionNote onView={() => setViewingShared(groupForm.playlistId)} />
            )}
          </FormField>
        </div>
      </Modal>

      {/* ── Manage members dialog ── */}
      <Modal
        isOpen={!!membersGroup}
        onClose={() => setMembersGroup(null)}
        title={`Manage screens — ${membersGroup?.name ?? ''}`}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setMembersGroup(null)}>
              Cancel
            </Button>
            <Button onClick={saveMembers} loading={savingMembers}>
              Save
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-slate-500">Only screens at this group's location can be members.</p>
        <SearchableMultiSelect
          options={
            membersGroup
              ? screens
                  .filter((s) => s.locationId === membersGroup.locationId)
                  .map((s) => ({ value: String(s.id), label: s.name }))
              : []
          }
          selectedValues={memberIds}
          onSelectionChange={setMemberIds}
          placeholder="Select screens..."
          searchPlaceholder="Search screens..."
        />
      </Modal>

      {/* ── Confirmations ── */}
      <ConfirmDialog
        isOpen={!!deleteScreenTarget}
        onClose={() => setDeleteScreenTarget(null)}
        onConfirm={doDeleteScreen}
        title={`Delete “${deleteScreenTarget?.name}”?`}
        message="Any paired Apple TV is automatically unpaired and returns to its pairing screen."
        confirmLabel="Delete"
      />
      <ConfirmDialog
        isOpen={!!unpairTarget}
        onClose={() => setUnpairTarget(null)}
        onConfirm={doUnpair}
        title="Unpair this device?"
        message="The Apple TV will stop playback and return to the pairing screen. The screen record stays."
        confirmLabel="Unpair"
        variant="warning"
      />
      <ConfirmDialog
        isOpen={!!deleteGroupTarget}
        onClose={() => setDeleteGroupTarget(null)}
        onConfirm={doDeleteGroup}
        title={`Delete “${deleteGroupTarget?.name}”?`}
        message="Its screens stay, and fall back to their own playlist."
        confirmLabel="Delete"
      />
      <ConfirmDialog
        isOpen={pendingSwitch !== null}
        onClose={() => setPendingSwitch(null)}
        onConfirm={() => {
          pendingSwitch?.();
          setPendingSwitch(null);
        }}
        title="Change the playlist?"
        message={SHARED_PLAYLIST_WARNING}
        confirmLabel="Change playlist"
        variant="warning"
      />
      {viewingShared !== null && (
        <SharedPlaylistViewer
          playlistId={viewingShared}
          entry={shared[viewingShared]}
          onClose={() => setViewingShared(null)}
        />
      )}
    </AppleTvPageShell>
  );
}

/** Under a playlist picker whose current value an administrator assigned. */
function SharedSelectionNote({ onView }: { onView: () => void }) {
  return (
    <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
      <SharedPlaylistBadge />
      <span>Read-only.</span>
      <button type="button" className="font-medium text-primary hover:underline" onClick={onView}>
        View slides
      </button>
    </p>
  );
}
